import React, { useState, useEffect } from "react";
import { showConfirm, showSuccess, showError } from "component/utils/SweetAlert";
import axios from "axios";
import DataTable from "react-data-table-component";
import DatatableStyles from 'component/utils/DatatableStyles';
import { formatRupiah } from "component/utils/formatRupiah";


const paginationOptions = {
    rowsPerPageText: "Show Per Page:", 
    rangeSeparatorText: "dari",
  };

const ItemPrice = ({itemId, itemData, onBackToItem, onSuccess}) => {
    const [search, setSearch] = useState("");

    const formattedData = itemData.map((item, index) => ({
      id: index + 1,
      ...item
    }));

    const filteredData = formattedData.filter((item) =>
      [item?.day_type, item?.price]
        .some((field) => field?.toString().toLowerCase().includes(search.toLowerCase()))
    );

    const columns = [
      {
        name: "No",
        selector: (row) => row.id,
        width: "70px",
        sortable: true,
        center: true,
      },
      {
        name: "Day Type",
        selector: (row) => row.day_type,
        sortable: true,
        minWidth: "120px",
        cell: (row) => (
          <span className="text-capitalize">
            {row.day_type}
          </span>
        ),
      },
      {
        name: "Price",
        selector: (row) => row.price,
        sortable: true,
        minWidth: "150px",
        right: true,
        cell: (row) => (
          <strong>
            Rp {new Intl.NumberFormat("id-ID").format(row.price)}
          </strong>
        ),
      },
      {
        name: "Start Date",
        selector: (row) => row.start_date,
        minWidth: "130px",
        cell: (row) =>
          row.start_date ? row.start_date : "-",
      },
      {
        name: "End Date",
        selector: (row) => row.end_date,
        minWidth: "130px",
        cell: (row) =>
          row.end_date ? row.end_date : "-",
      },
      {
        name: "Status",
        selector: (row) => row.status,
        minWidth: "100px",
        cell: (row) => (
          <span
            className={`badge ${
              row.status === "ACTIVE"
                ? "bg-success"
                : "bg-secondary"
            }`}
          >
            {row.status}
          </span>
        ),
      },
      {
        name: "Action",
        minWidth: "130px",
        center: true,
        cell: (row) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-warning rounded-circle"
              onClick={() => handleEditPriceItem(row)}
            >
              <i className="bx bx-edit text-white"></i>
            </button>

            <button
              className="btn btn-sm btn-danger rounded-circle"
              onClick={() => handleDeletePriceItem(row)}
            >
              <i className="bx bx-trash"></i>
            </button>
          </div>
        ),
      },
    ];

    const [mode, setMode] = useState("add");
    const [selectedPriceItem, setSelectedPriceItem] = useState(null);
    const [formData, setFormData] = useState({
      item_id: itemId,
      day_type: "",
      price: "",
      start_date: "",
      end_date: "",
    });

    const showDateRange= formData.day_type && !["weekday", "weekend"].includes(formData.day_type);

    useEffect(() => {
      if (mode === "edit" && selectedPriceItem) {
        setFormData({
          item_id: selectedPriceItem.item_id,
          day_type: selectedPriceItem.day_type,
          price: selectedPriceItem.price,
          start_date: selectedPriceItem.start_date || "",
          end_date: selectedPriceItem.end_date || "",
        });
      } else if (itemId) {
        setFormData({
          item_id: itemId,
          day_type: "",
          price: "",
          start_date: "",
          end_date: "",
        });
      }
    }, [mode, selectedPriceItem, itemId]);
  
    const handleChange = (e) => {
      const { name, value } = e.target;
  
      if (name === "price") {
        const numericValue = value.replace(/[^0-9]/g, "");
        setFormData({
          ...formData,
          [name]: formatRupiah(numericValue),
        });
      } else {
        setFormData({
          ...formData,
          [name]: value,
        });
      }
    };
  
    const handleSubmit = async (e) => {
      e.preventDefault();
      
      try {
        if (mode === "add") {
          const response = await axios.post(`${process.env.REACT_APP_API}/item-price/add`, {
            item_id: formData.item_id,
            day_type: formData.day_type,
            price: formData.price.replace(/[^0-9]/g, ""),
            start_date: showDateRange ? formData.start_date : null,
            end_date: showDateRange ? formData.end_date : null,
          });
          if (response.status === 201) {
            showSuccess("Item Price berhasil ditambahkan");
            if (onSuccess) onSuccess(formData.item_id);
            setFormData({
              item_id: itemId,
              price_id: "",
              qty: "",
            });
          } else {
            showError("Gagal menambahkan Item Price");
          }
        } else {
          const response = await axios.put(
            `${process.env.REACT_APP_API}/item-price/${formData.item_price_id}`,
            {
              item_price_id: formData.item_price_id,
              item_id: formData.item_id,
              day_type: formData.day_type,
              price: formData.price.replace(/[^0-9]/g, ""),
              start_date: showDateRange ? formData.start_date : null,
              end_date: showDateRange ? formData.end_date : null,
            }
          );
          if (response.status === 200) {
            showSuccess("Item Price berhasil diperbarui");
            if (onSuccess) onSuccess(formData.item_id);
            setMode("add");
          } else {
            showError("Gagal memperbarui Item Price");
          }
        }
      } catch (error) {
        console.error("Error:", error.response?.data || error.message);
        showError(error.response?.data?.message || "Terjadi kesalahan saat menyimpan data.");
      }
    };
  
    const handleEditPriceItem = (item) => {
      setMode("edit");
      setSelectedPriceItem(item);
    };

    const handleAddPriceItem = () => {
      setMode("add");
      setSelectedPriceItem("");
    };

    const handleDeletePriceItem = async (item) => {
      try {
        const confirmed = await showConfirm({
          title: 'Hapus Item Price',
          text: `Apakah Anda yakin ingin menghapus Item Price ${item.day_type}?`,
          confirmButtonText: 'Hapus'
        });
        if (!confirmed) return;
        if (!item?.price_id) {
          alert("Item Price ID tidak ditemukan");
          return;
        }

        const response = await axios.delete(
          `${process.env.REACT_APP_API}/item-price/${item.price_id}`
        );

        if (response.status === 200 || response.status === 201) {
          showSuccess(response.data.message || "Item Price berhasil dihapus");
          if (onSuccess) onSuccess(item.item_id);
        } else {
          showError(response.data.message || "Gagal menghapus Item Price");
        }
      } catch (error) {
        console.error("Error deleting Item Price:", error);
        showError(error.response?.data?.message || "Terjadi kesalahan saat menghapus Item Price");
      }
    };

  return (
    <div class="row">
      <div class="col-lg-5">
        <form onSubmit={handleSubmit}>
          <input type="hidden" name="item_id" value={formData.item_id} />

          {/* DAY TYPE */}
          <div className="form-group mb-3">
            <label className="form-label">Day Type</label>
            <select
              className="form-select"
              name="day_type"
              value={formData.day_type}
              onChange={handleChange}
              required
            >
              <option value="" hidden>-- Pilih Day Type --</option>
              <option value="weekday">Weekday</option>
              <option value="weekend">Weekend</option>
              <option value="session">Session</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {/* PRICE */}
          <div class="form-group mb-3">
            <label for="price" class="form-label">Price</label>
            <input 
              class="form-control"
              type="text" 
              name="price"
              placeholder="Isi Price"
              value={formData.price}
              onChange={handleChange}
              required
            />
          </div>

          {/* DATE RANGE */}
          {showDateRange && (
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          )}

          <button
            type="button"
            className="btn btn-primary me-2"
            onClick={onBackToItem}
          >
            Back To Item
          </button>

          {mode === "edit" && (
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={handleAddPriceItem}
            >
              Back To Add
            </button>
          )}

          <button type="submit" className="btn btn-success">
            {mode === "add" ? "Save" : "Update"}
          </button>
        </form>
      </div>

      
      <div class="col-lg-7 mb-4">
          <div class="d-flex justify-content-end align-items-center mb-2">
                  <input
                  type="text"
                  placeholder="Search..."
                  class="form-control w-100"
                  style={{ maxWidth: "200px" }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  />
          </div>

          <div class="datatable-container">
              <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover={false}
              customStyles={DatatableStyles}
              paginationPerPage={5}
              paginationRowsPerPageOptions={[5, 10, 25]}
              paginationComponentOptions={paginationOptions}
              responsive
              />
          </div>
      </div>
    </div>
  );
};

export default ItemPrice;
